import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import useNetworkStatus from '../hooks/useNetworkStatus';
import logger from '../utils/logger';

const GlobalWebSocketContext = createContext();

export const useGlobalWebSocket = () => {
  const context = useContext(GlobalWebSocketContext);
  if (!context) {
    throw new Error('useGlobalWebSocket must be used within a GlobalWebSocketProvider');
  }
  return context;
};

export const GlobalWebSocketProvider = ({ children }) => {
  logger.websocket('GlobalWebSocketProvider mounting');
  
  const [, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const subscribedTripsRef = useRef(new Set());
  const eventListeners = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const heartbeatIntervalRef = useRef(null);
  const lastPongRef = useRef(Date.now());
  
  // NEW: Connection state management to prevent race conditions
  const isConnectingRef = useRef(false);
  const connectionDebounceRef = useRef(null);
  const hasInitializedRef = useRef(false);
  
  // Network status monitoring
  const { isOnline } = useNetworkStatus();
  console.log('🚨 CRITICAL: Network status in GlobalWebSocket:', { isOnline, navigatorOnline: navigator.onLine });

  // JWT token validation - wrapped in useCallback to prevent re-creation
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const isExpired = payload.exp < currentTime;
      
      console.log('🔑 Token expiration check:', {
        exp: payload.exp,
        current: currentTime,
        expired: isExpired,
        expiresIn: Math.round((payload.exp - currentTime) / 60) + ' minutes'
      });
      
      return isExpired;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }, []);

  // Add event listener
  const addEventListener = useCallback((eventType, listener) => {
    if (!eventListeners.current.has(eventType)) {
      eventListeners.current.set(eventType, new Set());
    }
    eventListeners.current.get(eventType).add(listener);
    
    return () => {
      const listeners = eventListeners.current.get(eventType);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          eventListeners.current.delete(eventType);
        }
      }
    };
  }, []);

  // Subscribe to a trip - avoid duplicate subscriptions
  const subscribeToTrip = useCallback((tripId) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const tripIdStr = tripId.toString();
      // Only subscribe if not already subscribed
      if (!subscribedTripsRef.current.has(tripIdStr)) {
        subscribedTripsRef.current.add(tripIdStr);
        socketRef.current.send(JSON.stringify({
          type: 'subscribe_trip',
          tripId: tripId
        }));
        return true;
      } else {
        return true; // Already subscribed is considered success
      }
    }
    return false;
  }, []);

  // Unsubscribe from a trip
  const unsubscribeFromTrip = useCallback((tripId) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      subscribedTripsRef.current.delete(tripId.toString());
      socketRef.current.send(JSON.stringify({
        type: 'unsubscribe_trip',
        tripId: tripId
      }));
      return true;
    }
    return false;
  }, []);

  // Send message to a specific trip with retry logic
  const sendMessage = useCallback((tripId, content) => {
    // Validate inputs
    if (!tripId || !content || typeof content !== 'string' || !content.trim()) {
      console.warn('⚠️ Invalid message parameters:', { tripId, content: typeof content });
      return false;
    }

    // Check message size limit (1MB for WebSocket messages)
    const maxMessageSize = 1024 * 1024; // 1MB
    if (content.length > maxMessageSize) {
      console.warn('⚠️ Message too large:', content.length, 'bytes, max:', maxMessageSize);
      return false;
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      try {
        const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${(typeof performance !== 'undefined' && performance.now) ? performance.now() : Math.random()}`;
        const messageData = {
          type: 'chat_message',
          tripId: tripId,
          content: content.trim(),
          messageId: messageId,
          timestamp: new Date().toISOString()
        };
        
        // Double-check connection state right before sending
        if (socketRef.current.readyState !== WebSocket.OPEN) {
          console.warn('⚠️ WebSocket state changed during send attempt');
          return false;
        }
        
        socketRef.current.send(JSON.stringify(messageData));
        console.log(`📤 Message sent via WebSocket:`, { tripId, messageId, content: content.substring(0, 50) + '...' });
        return true;
      } catch (error) {
        console.error('❌ Error sending WebSocket message:', error);
        return false;
      }
    } else {
      console.warn('⚠️ WebSocket not ready for sending:', {
        exists: !!socketRef.current,
        readyState: socketRef.current?.readyState,
        expectedState: WebSocket.OPEN
      });
      return false;
    }
  }, []);

  // Close connection
  const closeConnection = useCallback(() => {
    isUnmountingRef.current = true;
    isConnectingRef.current = false;
    
    // Clear any pending connection attempts
    if (connectionDebounceRef.current) {
      clearTimeout(connectionDebounceRef.current);
      connectionDebounceRef.current = null;
    }
    
    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Clear heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (socketRef.current) {
      try {
        socketRef.current.close(1000, 'Manual close');
      } catch (error) {
        console.log('Error closing global WebSocket:', error.message);
      }
      socketRef.current = null;
    }
    setSocket(null);
    setConnected(false);
    subscribedTripsRef.current.clear();
  }, [setSocket, setConnected]);

  // Internal setup function - useCallback to prevent recreation
  const setupWebSocketConnection = useCallback(() => {
    // ENHANCED DEBUG LOGGING with connection state check
    console.group('🔧 Global WebSocket Setup Debug');
    console.log('Component unmounting?', isUnmountingRef.current);
    console.log('Already connecting?', isConnectingRef.current);
    console.log('Current socket state:', socketRef.current?.readyState);
    console.log('Network online?', isOnline);
    
    // NEW: Prevent multiple simultaneous connections
    if (isUnmountingRef.current) {
      console.log('❌ Component unmounting - skipping connection');
      console.groupEnd();
      return;
    }
    
    if (isConnectingRef.current) {
      console.log('❌ Already connecting - skipping duplicate connection attempt');
      console.groupEnd();
      return;
    }
    
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected - skipping');
      console.groupEnd();
      return;
    }
    
    if (!isOnline) {
      console.log('❌ Network offline - skipping connection');
      console.groupEnd();
      return;
    }

    // Set connecting flag
    isConnectingRef.current = true;

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('❌ No token found');
      console.groupEnd();
      isConnectingRef.current = false;
      setError('Please log in to continue');
      return;
    }

    // Check if token is expired before attempting connection
    if (isTokenExpired(token)) {
      console.log('❌ Token expired');
      console.groupEnd();
      isConnectingRef.current = false;
      setError('Session expired. Please log in again.');
      return;
    }

    // Generate WebSocket URL for global connection with Safari compatibility
    const getWebSocketURL = () => {
      let baseUrl;
      
      // Safari-specific URL handling
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (process.env.NODE_ENV === 'production') {
        // In production, derive from API URL or use Railway default
        baseUrl = process.env.REACT_APP_API_URL || 'https://trip-tribe-app-production.up.railway.app';
      } else {
        // In development, handle Safari's stricter requirements
        if (isSafari && window.location.protocol === 'https:') {
          // If running on HTTPS (even localhost), Safari needs WSS
          baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:5002';
        } else {
          // Normal development
          baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';
        }
      }
      
      console.log('Base URL chosen:', baseUrl);
      console.log('Safari detected?', isSafari);
      
      // Convert HTTP(S) to WS(S)
      let wsUrl = baseUrl.replace(/^https?:\/\//, (match) => {
        return match === 'https://' ? 'wss://' : 'ws://';
      });
      
      // Remove /api suffix if present (WebSocket doesn't use /api prefix)
      wsUrl = wsUrl.replace(/\/api$/, '');
      
      console.log('WebSocket URL generated:', wsUrl);
      return wsUrl;
    };
    
    const wsUrl = getWebSocketURL();
    const fullUrl = `${wsUrl}/ws/global?token=${encodeURIComponent(token)}`;
    console.log('Final WebSocket URL:', fullUrl);
    console.groupEnd();

    let socket;
    try {
      // Safari-specific WebSocket creation
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      console.log('🔌 Creating WebSocket connection...');
      console.log('🦎 Safari mode?', isSafari);
      
      socket = new WebSocket(fullUrl);
      socketRef.current = socket;
      setSocket(socket);
      
      console.log('✅ WebSocket object created successfully');
      console.log('Initial readyState:', socket.readyState);
    } catch (error) {
      console.group('❌ Safari WebSocket Creation Error');
      console.error('WebSocket creation failed:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      console.error('URL that failed:', fullUrl);
      console.groupEnd();
      isConnectingRef.current = false;
      setError('Failed to create WebSocket connection');
      return;
    }

    socket.onopen = () => {
      console.log('✅ Global WebSocket connected successfully');
      console.log('✅ Connection URL was:', fullUrl);
      console.log('✅ Socket readyState:', socket.readyState);
      
      isConnectingRef.current = false; // Clear connecting flag
      setConnected(true);
      setError(null);
      reconnectAttempts.current = 0;
      lastPongRef.current = Date.now();

      // IMPROVED: Less aggressive heartbeat (30 second intervals instead of 15)
      heartbeatIntervalRef.current = setInterval(() => {
        // Check if we're still mounted and socket is valid
        if (isUnmountingRef.current || !socketRef.current) {
          return;
        }
        
        if (socketRef.current.readyState === WebSocket.OPEN) {
          const timeSinceLastPong = Date.now() - lastPongRef.current;
          
          // IMPROVED: Increased timeout to 60 seconds (instead of 30)
          if (timeSinceLastPong > 60000) {
            console.warn('🔴 WebSocket connection appears stale, forcing reconnection');
            try {
              socketRef.current.close();
            } catch (error) {
              console.error('Error closing stale connection:', error);
            }
            return;
          }
          
          // Send ping
          try {
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
          } catch (error) {
            console.error('Failed to send ping:', error);
          }
        } else {
          console.log('🔴 WebSocket not in OPEN state during heartbeat, state:', socketRef.current.readyState);
        }
      }, 30000); // IMPROVED: Ping every 30 seconds instead of 15

      // Re-subscribe to any previously subscribed trips
      subscribedTripsRef.current.forEach(tripId => {
        socket.send(JSON.stringify({
          type: 'subscribe_trip',
          tripId: tripId
        }));
      });
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'connection_success') {
        } else if (data.type === 'subscription_success') {
        } else if (data.type === 'pong') {
          // Update last pong timestamp for heartbeat
          lastPongRef.current = Date.now();
        } else if (data.type === 'new_message') {
          // Handle incoming messages
          
          // Notify listeners for 'message' event type
          const messageListeners = eventListeners.current.get('message');
          if (messageListeners) {
            messageListeners.forEach(listener => {
              try {
                listener(data);
              } catch (error) {
                console.error('Error in global WebSocket event listener:', error);
              }
            });
          }
        } else {
          // Handle all other event types generically
          const listeners = eventListeners.current.get(data.type);
          if (listeners) {
            listeners.forEach(listener => {
              try {
                listener(data);
              } catch (error) {
                console.error('Error in global WebSocket event listener:', error);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error parsing global WebSocket message:', error);
      }
    };

    socket.onclose = (event) => {
      console.group('🔴 Global WebSocket Connection Closed');
      console.log('Close code:', event.code);
      console.log('Close reason:', event.reason);
      console.log('Was clean?:', event.wasClean);
      console.log('Connection URL was:', fullUrl);
      console.log('Reconnect attempts:', reconnectAttempts.current);
      console.groupEnd();
      
      isConnectingRef.current = false; // Clear connecting flag
      setConnected(false);
      socketRef.current = null;
      setSocket(null);

      // Clear heartbeat interval to prevent memory leaks
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // IMPROVED: Only reconnect for unexpected disconnections and when online
      if (!isUnmountingRef.current && event.code !== 1000 && isOnline && reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(`🌐 Attempting to reconnect global WebSocket in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!isUnmountingRef.current && isOnline) {
            // Direct reconnection to avoid circular dependency
            setupWebSocketConnection();
          }
        }, delay);
      } else if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.error('🌐 Max reconnection attempts reached for global WebSocket');
        setError('Connection lost. Please refresh the page.');
      }
    };

    socket.onerror = (error) => {
      console.group('❌ Global WebSocket Error Details');
      console.error('Error event:', error);
      console.error('Socket readyState:', socket.readyState);
      console.error('Connection URL:', fullUrl);
      console.error('Error type:', error.type);
      console.error('Error target:', error.target);
      console.error('Network online?', navigator.onLine);
      console.groupEnd();
      
      isConnectingRef.current = false; // Clear connecting flag
      setError('WebSocket connection error');
    };
  }, [isOnline, isTokenExpired, setError, setConnected, setSocket]);

  // Debounced setup function to prevent rapid reconnection attempts
  const debouncedSetupWebSocket = useCallback(() => {
    // Clear any existing debounce timeout
    if (connectionDebounceRef.current) {
      clearTimeout(connectionDebounceRef.current);
      connectionDebounceRef.current = null;
    }
    
    console.log('🎯 Setting up debounced WebSocket connection (100ms delay)');
    connectionDebounceRef.current = setTimeout(() => {
      console.log('🚨 CRITICAL: Debounce timeout fired - calling setupWebSocketInternal');
      if (!isUnmountingRef.current && !isConnectingRef.current) {
        // Call the internal setup function directly
        setupWebSocketConnection();
      } else {
        console.log('🎯 Skipping setup - unmounting:', isUnmountingRef.current, 'connecting:', isConnectingRef.current);
      }
    }, 100); // 100ms debounce
  }, [setupWebSocketConnection]); // Add setupWebSocketConnection as dependency

  // Handle token expiry
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('🌐 Token expired - closing global WebSocket connection');
      closeConnection();
      setError('Session expired. Please log in again.');
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [closeConnection, setError]);

  console.log('🚨 CRITICAL: About to define initialization useEffect');
  
  // SIMPLIFIED: Single initialization effect with enhanced debugging
  useEffect(() => {
    console.log('🚨 CRITICAL: Initialization useEffect is EXECUTING!');
    
    // CRITICAL FIX: Reset unmounting flag on remount (React Strict Mode issue)
    isUnmountingRef.current = false;
    console.log('🚨 CRITICAL: Reset unmounting flag to false');
    
    console.group('🔧 GlobalWebSocket Initialization Debug');
    const token = localStorage.getItem('token');
    console.log('Raw token from localStorage:', token ? token.substring(0, 50) + '...' : 'null');
    console.log('Has token?', !!token);
    console.log('Token length:', token ? token.length : 0);
    console.log('Token expired?', token ? isTokenExpired(token) : 'No token');
    console.log('Network online?', isOnline);
    console.log('isConnectingRef.current:', isConnectingRef.current);
    console.log('connected:', connected);
    console.log('Will attempt connection?', !!(token && !isTokenExpired(token) && isOnline));
    console.groupEnd();
    
    if (token && !isTokenExpired(token) && isOnline) {
      console.log('🚀 Attempting WebSocket connection from initialization');
      console.log('🎯 Calling debouncedSetupWebSocket...');
      debouncedSetupWebSocket();
    } else {
      console.warn('❌ WebSocket connection not attempted:', {
        hasToken: !!token,
        tokenExpired: token ? isTokenExpired(token) : 'no token',
        isOnline,
        reason: !token ? 'No token' : isTokenExpired(token) ? 'Token expired' : !isOnline ? 'Offline' : 'Unknown'
      });
    }

    // Mark as initialized after the first run
    hasInitializedRef.current = true;

    return () => {
      isUnmountingRef.current = true;
      closeConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // IMPROVED: Better network status handling - only reconnect when network comes back online
  useEffect(() => {
    // CRITICAL FIX: Don't interfere with initial setup
    if (!hasInitializedRef.current) {
      console.log('🚨 Network status skipping - waiting for initial setup to complete');
      return;
    }
    
    // Only trigger when network comes back online (not when connection state changes)
    if (isOnline && !isConnectingRef.current && !isUnmountingRef.current) {
      // Check if we need to reconnect
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        const token = localStorage.getItem('token');
        if (token && !isTokenExpired(token)) {
          console.log('🌐 Network back online and not connected - attempting reconnection');
          debouncedSetupWebSocket();
        }
      }
    }
  }, [isOnline, debouncedSetupWebSocket, isTokenExpired]); // Removed 'connected' dependency

  // IMPROVED: Better token change handling
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue && !isTokenExpired(e.newValue)) {
          // New token available, reconnect if not already connected
          if ((!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) && !isConnectingRef.current) {
            debouncedSetupWebSocket();
          }
        } else {
          // Token removed or expired, close connection
          closeConnection();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [debouncedSetupWebSocket, closeConnection, isTokenExpired]); // Removed 'connected' dependency

  // Add global event listener to log all events
  const addGlobalEventListener = useCallback((listener) => {
    if (!socketRef.current) return () => {};
    socketRef.current.addEventListener('message', listener);
    return () => {
      if (socketRef.current) {
        socketRef.current.removeEventListener('message', listener);
      }
    };
  }, []);

  const value = {
    connected,
    error,
    addEventListener,
    subscribeToTrip,
    unsubscribeFromTrip,
    sendMessage,
    reconnect: debouncedSetupWebSocket,
    close: closeConnection,
    subscribedTrips: Array.from(subscribedTripsRef.current),
    addGlobalEventListener
  };

  return (
    <GlobalWebSocketContext.Provider value={value}>
      {children}
    </GlobalWebSocketContext.Provider>
  );
};

export default GlobalWebSocketProvider; 